/**
 * Proof-of-play on-screen duration measurement (client).
 *
 * Wall clock (`Date.now`) can jump forward/back on Android/signage devices; measuring
 * play duration with it produced multi-day inflated `durationSeconds` (INC-20261022-POP-1D7D).
 * Use a monotonic clock (`performance.now`) for deltas; keep wall clock only for `playedAt`.
 *
 * Aligns with server ingest ceiling {@link POP_INGEST_MAX_PLAY_DURATION_SECONDS} (900 s /
 * one 15-min wall bucket) from `popIngestGuards` (#2745).
 */

/** Max seconds one play / flush entry may claim (matches API per-play ceiling). */
export const POP_CLIENT_MAX_PLAY_DURATION_SECONDS = 900 as const

/**
 * Extra seconds allowed above the item's scheduled duration (crossfade / sticky hold /
 * timer jitter). Still hard-capped at {@link POP_CLIENT_MAX_PLAY_DURATION_SECONDS}.
 */
export const POP_CLIENT_SCHEDULED_DURATION_TOLERANCE_SECONDS = 2 as const

export type TProofOfPlayDurationClampReason =
  | 'negative_or_nan'
  | 'scheduled_ceiling'
  | 'absolute_ceiling'

export type TProofOfPlayDeltaMsResult = {
  /** Clamped elapsed ms to accumulate, or `null` when the sample must be dropped. */
  deltaMs: number | null
  /** Raw monotonic delta before clamp (may be negative / NaN). */
  rawDeltaMs: number
  clamped: boolean
  reason?: TProofOfPlayDurationClampReason
  /** Ceiling applied when clamped to a positive value (seconds). */
  ceilingSeconds?: number
}

export type TProofOfPlayDurationLogEvent = {
  kind: 'drop' | 'clamp'
  reason: TProofOfPlayDurationClampReason
  rawDeltaMs: number
  deltaMs: number | null
  scheduledDurationSeconds: number | null
  ceilingSeconds: number | null
}

function defaultLog(event: TProofOfPlayDurationLogEvent): void {
  console.warn(JSON.stringify({
    tag: 'proof_of_play_duration',
    ...event
  }))
}

/**
 * Cap for one measurement interval: min(scheduled + tolerance, absolute max).
 * When scheduled duration is missing / non-positive, only the absolute max applies.
 */
export function proofOfPlayDeltaCeilingSeconds(
  scheduledDurationSeconds: number | null | undefined,
  opts?: {
    maxPlaySeconds?: number
    toleranceSeconds?: number
  }
): number {
  const maxPlay = opts?.maxPlaySeconds ?? POP_CLIENT_MAX_PLAY_DURATION_SECONDS
  const tol = opts?.toleranceSeconds ?? POP_CLIENT_SCHEDULED_DURATION_TOLERANCE_SECONDS
  const abs = Number.isFinite(maxPlay) && maxPlay > 0
    ? Math.trunc(maxPlay)
    : POP_CLIENT_MAX_PLAY_DURATION_SECONDS
  if (
    scheduledDurationSeconds == null
    || !Number.isFinite(scheduledDurationSeconds)
    || scheduledDurationSeconds <= 0
  ) {
    return abs
  }
  const scheduledCap = Math.ceil(scheduledDurationSeconds + tol)
  return Math.min(abs, Math.max(1, scheduledCap))
}

/**
 * Convert a monotonic interval into ms to accumulate for PoP.
 * Negative / NaN → drop (`deltaMs: null`). Oversized → clamp + log.
 */
export function measureProofOfPlayDeltaMs(args: {
  startMonoMs: number
  endMonoMs: number
  scheduledDurationSeconds?: number | null
  maxPlaySeconds?: number
  toleranceSeconds?: number
  onEvent?: (event: TProofOfPlayDurationLogEvent) => void
}): TProofOfPlayDeltaMsResult {
  const log = args.onEvent ?? defaultLog
  const rawDeltaMs = args.endMonoMs - args.startMonoMs
  if (!Number.isFinite(rawDeltaMs) || rawDeltaMs < 0) {
    const result: TProofOfPlayDeltaMsResult = {
      deltaMs: null,
      rawDeltaMs,
      clamped: true,
      reason: 'negative_or_nan'
    }
    log({
      kind: 'drop',
      reason: 'negative_or_nan',
      rawDeltaMs,
      deltaMs: null,
      scheduledDurationSeconds: args.scheduledDurationSeconds ?? null,
      ceilingSeconds: null
    })
    return result
  }
  if (rawDeltaMs === 0) {
    return { deltaMs: 0, rawDeltaMs, clamped: false }
  }

  const ceilingSeconds = proofOfPlayDeltaCeilingSeconds(args.scheduledDurationSeconds, {
    maxPlaySeconds: args.maxPlaySeconds,
    toleranceSeconds: args.toleranceSeconds
  })
  const ceilingMs = ceilingSeconds * 1000
  if (rawDeltaMs <= ceilingMs) {
    return { deltaMs: rawDeltaMs, rawDeltaMs, clamped: false }
  }

  const reason: TProofOfPlayDurationClampReason
    = args.scheduledDurationSeconds != null
      && Number.isFinite(args.scheduledDurationSeconds)
      && args.scheduledDurationSeconds > 0
      && ceilingSeconds < (args.maxPlaySeconds ?? POP_CLIENT_MAX_PLAY_DURATION_SECONDS)
      ? 'scheduled_ceiling'
      : 'absolute_ceiling'

  const result: TProofOfPlayDeltaMsResult = {
    deltaMs: ceilingMs,
    rawDeltaMs,
    clamped: true,
    reason,
    ceilingSeconds
  }
  log({
    kind: 'clamp',
    reason,
    rawDeltaMs,
    deltaMs: ceilingMs,
    scheduledDurationSeconds: args.scheduledDurationSeconds ?? null,
    ceilingSeconds
  })
  return result
}

/**
 * Final whole-seconds value for one heartbeat `assetPlays` entry.
 * Drops non-finite / non-positive; clamps above {@link POP_CLIENT_MAX_PLAY_DURATION_SECONDS}.
 */
export function finalizeProofOfPlayDurationSeconds(
  durationMs: number,
  opts?: {
    maxPlaySeconds?: number
    onEvent?: (event: TProofOfPlayDurationLogEvent) => void
  }
): number | null {
  const log = opts?.onEvent ?? defaultLog
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    log({
      kind: 'drop',
      reason: 'negative_or_nan',
      rawDeltaMs: durationMs,
      deltaMs: null,
      scheduledDurationSeconds: null,
      ceilingSeconds: null
    })
    return null
  }
  const maxPlay = opts?.maxPlaySeconds ?? POP_CLIENT_MAX_PLAY_DURATION_SECONDS
  const rounded = Math.max(1, Math.round(durationMs / 1000))
  if (!Number.isFinite(rounded) || rounded <= 0) {
    log({
      kind: 'drop',
      reason: 'negative_or_nan',
      rawDeltaMs: durationMs,
      deltaMs: null,
      scheduledDurationSeconds: null,
      ceilingSeconds: null
    })
    return null
  }
  if (rounded <= maxPlay) {
    return rounded
  }
  log({
    kind: 'clamp',
    reason: 'absolute_ceiling',
    rawDeltaMs: durationMs,
    deltaMs: maxPlay * 1000,
    scheduledDurationSeconds: null,
    ceilingSeconds: maxPlay
  })
  return maxPlay
}

/** Monotonic clock for PoP duration (injectable in tests). */
export function defaultMonotonicNowMs(): number {
  return performance.now()
}

/** Wall clock for `playedAt` only (injectable in tests). */
export function defaultWallNowMs(): number {
  return Date.now()
}
